import { motion } from "framer-motion";
import { MessageCircle, Phone, FileCheck, Handshake } from "lucide-react";
import { useTranslation } from "../i18n/LanguageContext";

const STEP_ICONS = [MessageCircle, Phone, FileCheck, Handshake];
const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.15 } } };
const item = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

export default function ProcessSection() {
  const { t } = useTranslation();
  const p = t.process;

  return (
    <section id="process" className="py-24 md:py-32 bg-[#0A0A0B]" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-6">
            <motion.span
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="inline-block w-10 h-px bg-[#C9A96E]"
              style={{ transformOrigin: "left center" }}
            />
            <span className="fwd-eyebrow">{p.eyebrow}</span>
          </div>
          <h2 className="fwd-heading">
            {p.headline1}
            <br />
            {p.headline2} <span className="fwd-italic-gold">{p.headlineItalic}</span>
          </h2>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="relative"
        >
          <div className="hidden lg:block absolute left-[39px] top-20 bottom-20 w-px">
            <div className="h-full" style={{ background: "linear-gradient(to bottom, #C9A96E, rgba(201,169,110,0.3), #2A2A2E)" }} />
          </div>

          <div className="space-y-6 lg:space-y-8">
            {p.steps.map((step, index) => {
              const Icon = STEP_ICONS[index] || MessageCircle;
              const number = String(index + 1).padStart(2, "0");
              return (
                <motion.div
                  key={index}
                  variants={item}
                  className="relative flex items-start gap-6 lg:gap-10"
                  data-testid={`process-step-${index}`}
                >
                  <div className="relative z-10 shrink-0">
                    <div
                      className="w-20 h-20 flex items-center justify-center"
                      style={{
                        background: "#141417",
                        border: "1px solid #2A2A2E",
                        borderRadius: "2px",
                      }}
                    >
                      <Icon size={26} style={{ color: "#C9A96E" }} strokeWidth={1.5} />
                    </div>
                  </div>

                  <div className="flex-1 pt-3">
                    <motion.span
                      initial={{ opacity: 0.3 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.7, ease: "easeOut" }}
                      className="fwd-eyebrow block mb-2"
                      style={{ fontSize: "16px", color: "#C9A96E" }}
                      data-testid={`process-step-number-${index}`}
                    >
                      {p.stepLabel(number)}
                    </motion.span>
                    <h3 className="fwd-heading mb-3" style={{ fontSize: "26px" }}>
                      {step.title}
                    </h3>
                    <p className="fwd-body" style={{ maxWidth: "640px" }}>
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
